import { useState } from "react";
import { Plus, X, Pencil, Check } from "lucide-react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useContactPositions, slugify } from "./PositionPicker";

// Simple editable list of positions offered as Title options when adding a contact.
const PositionsCard = ({ canEdit = true }) => {
  const { positions, save, reload } = useContactPositions();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null); // { from, to }

  const persist = async (next) => {
    setBusy(true);
    try { await save(next); return true; }
    catch (err) { toast.error(err.response?.data?.detail || "Could not save"); return false; }
    finally { setBusy(false); }
  };
  const add = async (e) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    if (positions.some((p) => p.toLowerCase() === name.toLowerCase())) { toast.error(`"${name}" is already listed`); return; }
    if (await persist([...positions, name].sort((a, b) => a.localeCompare(b)))) setDraft("");
  };

  const rename = async (e) => {
    e.preventDefault();
    const to = (renaming?.to || "").trim();
    if (!to || to === renaming.from) { setRenaming(null); return; }
    setBusy(true);
    try {
      const r = await apiClient.post("/shared-contacts/positions/rename", { old: renaming.from, new: to });
      toast.success(`Renamed to "${to}" — ${r.data.contacts_updated} contact${r.data.contacts_updated === 1 ? "" : "s"}${r.data.jobs_updated ? ` and ${r.data.jobs_updated} job${r.data.jobs_updated === 1 ? "" : "s"}` : ""} updated`);
      setRenaming(null);
      await reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not rename"); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="positions-card">
      <CardHeader className="py-3 flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Positions</CardTitle>
          <p className="text-sm text-muted-foreground">Title options offered when adding a contact.</p>
        </div>
        {canEdit && (
          <form onSubmit={add} className="flex items-center gap-2 w-64 shrink-0">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a position" className="h-9" autoComplete="off" data-testid="position-new-input" />
            <Button type="submit" size="sm" variant="outline" disabled={busy || !draft.trim()} data-testid="position-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </form>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y rounded-md border max-h-48 overflow-y-auto" data-testid="positions-list">
          {positions.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">No positions yet.</li>}
          {positions.map((p) => (
            <li key={p} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm" data-testid={`position-item-${slugify(p)}`}>
              {renaming?.from === p ? (
                <form onSubmit={rename} className="flex items-center gap-1 flex-1">
                  <Input autoFocus value={renaming.to} onChange={(e) => setRenaming({ ...renaming, to: e.target.value })} onKeyDown={(e) => { if (e.key === "Escape") setRenaming(null); }} className="h-7 text-sm" autoComplete="off" data-testid={`position-rename-input-${slugify(p)}`} />
                  <Button type="submit" size="icon" variant="ghost" className="h-7 w-7 text-emerald-700" disabled={busy} aria-label="Save name" data-testid={`position-rename-save-${slugify(p)}`}><Check className="w-3.5 h-3.5" /></Button>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setRenaming(null)} aria-label="Cancel" data-testid={`position-rename-cancel-${slugify(p)}`}><X className="w-3.5 h-3.5" /></Button>
                </form>
              ) : (
                <>
                  <span className="truncate">{p}</span>
                  {canEdit && (
                    <span className="flex items-center shrink-0">
                      <button type="button" onClick={() => setRenaming({ from: p, to: p })} disabled={busy} className="rounded p-1 text-slate-400 hover:text-sky-700 hover:bg-sky-50" aria-label={`Rename ${p}`} title="Rename (updates every contact with this title)" data-testid={`position-rename-${slugify(p)}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => persist(positions.filter((x) => x !== p))} disabled={busy} className="rounded p-1 text-slate-400 hover:text-red-600 hover:bg-red-50" aria-label={`Remove ${p}`} data-testid={`position-remove-${slugify(p)}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default PositionsCard;
